import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["buttonRef", "children", "disabled", "hint", "hintSuccess", "key", "onClick", "responsive", "tooltipPlacement", "size", "value", "href", "external", "use", "valueWhenCopied", "targetInputRef"];
/* eslint-disable @typescript-eslint/no-misused-promises */
import { useState, useRef, useEffect } from 'react';
import unescapedText from 'I18n/utils/unescapedText';
import devLogger from 'react-utils/devLogger';
import ShareButton from '../ShareButton/ShareButton';
import { Tooltip } from '../Tooltip/Tooltip';
import { lazyEval } from '../../utils/lazyEval';
import { Button } from '../Button/Button';
import { filterProps } from '../../../utils/filterProps';
import { jsx as _jsx } from "react/jsx-runtime";
const isHTMLElement = obj => {
  return typeof HTMLElement === 'object' ? obj instanceof HTMLElement //DOM2
  : obj && typeof obj === 'object' && obj !== null && obj.nodeType === 1 && typeof obj.nodeName === 'string';
};
export const InternalCopyButton = _ref => {
  let {
      buttonRef,
      children,
      disabled,
      hint = () => unescapedText('salesUI.UICopyInput.title'),
      hintSuccess = () => unescapedText('salesUI.UICopyInput.tooltipCopied'),
      key,
      onClick,
      responsive,
      tooltipPlacement = 'top',
      size,
      value,
      href,
      external,
      use = 'tertiary-light',
      valueWhenCopied = val => val,
      targetInputRef
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const [tooltipActive, setTooltipActive] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const ignoreNextClose = useRef(false);
  const resetTextTimeoutRef = useRef(null);
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  useEffect(() => {
    if (resetTextTimeoutRef.current) {
      clearTimeout(resetTextTimeoutRef.current);
    }
  }, []);
  const handleTooltipOpenChange = evt => {
    const wantsToOpen = evt.target.value;
    if (resetTextTimeoutRef.current) {
      clearTimeout(resetTextTimeoutRef.current);
      resetTextTimeoutRef.current = null;
    }
    if (wantsToOpen) {
      setIsOpen(true);
    } else {
      if (ignoreNextClose.current) {
        return;
      }
      setIsOpen(false);
      resetTextTimeoutRef.current = setTimeout(() => {
        setTooltipActive(false);
        resetTextTimeoutRef.current = null;
      }, 150);
    }
  };
  const copyWithWriteText = async text => {
    var _navigator$clipboard;
    if (!((_navigator$clipboard = navigator.clipboard) !== null && _navigator$clipboard !== void 0 && _navigator$clipboard.writeText)) {
      return false;
    }
    try {
      // eslint-disable-next-line compat/compat
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        devLogger.warn({
          key: 'CopyButton Clipboard API failed',
          message: 'CopyButton: Clipboard API failed, falling back to textarea method. ' + 'This can happen in iframes or when clipboard permissions are denied.'
        });
      }
      return false;
    }
  };
  const copyWithTextarea = text => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    document.body.appendChild(textarea);
    textarea.select();
    textarea.setSelectionRange(0, text.length);
    try {
      document.execCommand('copy');
      return true;
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        devLogger.warn({
          key: 'CopyButton fallback copy failed',
          message: 'CopyButton: Both Clipboard API and textarea fallback failed to copy text.'
        });
      }
      return false;
    } finally {
      document.body.removeChild(textarea);
    }
  };
  const handleClick = async evt => {
    if (resetTextTimeoutRef.current) {
      clearTimeout(resetTextTimeoutRef.current);
      resetTextTimeoutRef.current = null;
    }
    const copiedValue = valueWhenCopied(value);
    const isValueHTMLElement = isHTMLElement(copiedValue);
    const text = isValueHTMLElement ? copiedValue.textContent || '' : copiedValue;
    let copySuccess = await copyWithWriteText(text);
    if (!copySuccess) {
      copySuccess = copyWithTextarea(text);
    }
    if (copySuccess) {
      var _targetInputRef$curre;
      setTooltipActive(true);
      setIsOpen(true);
      ignoreNextClose.current = true;
      targetInputRef === null || targetInputRef === void 0 || (_targetInputRef$curre = targetInputRef.current) === null || _targetInputRef$curre === void 0 || _targetInputRef$curre.select();
      setTimeout(() => {
        ignoreNextClose.current = false;
      }, 0);
    } else {
      setTooltipActive(false);
      setIsOpen(false);
      ignoreNextClose.current = false;
    }
    if (onClick) onClick(evt);
  };
  const tooltipTitle = tooltipActive ? lazyEval(hintSuccess) : lazyEval(hint);
  const renderedButtonContent = children || /*#__PURE__*/_jsx("span", {
    children: unescapedText('salesUI.UICopyInput.label')
  });
  return /*#__PURE__*/_jsx(Tooltip, {
    disabled: !tooltipTitle || disabled,
    open: isOpen,
    onOpenChange: handleTooltipOpenChange,
    placement: tooltipPlacement,
    title: tooltipTitle,
    children: /*#__PURE__*/_jsx(Button, Object.assign({
      use: use,
      buttonRef: buttonRef,
      onClick: handleClick,
      disabled: disabled,
      responsive: responsive,
      size: size,
      href: href,
      external: external
    }, domProps, tokenProps, {
      children: renderedButtonContent
    }), key)
  });
};
export const CopyButton = ShareButton(InternalCopyButton);
CopyButton.displayName = 'CopyButton';